import { Plus } from 'lucide-react';
import { useState } from 'react';

import { errorMessage } from '../../lib/api.ts';
import { useRecordActivities, type RecordLink } from '../../lib/activities-api.ts';
import { useCan } from '../../lib/session.ts';
import { Alert } from '../ui/alert.tsx';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Loading } from '../ui/skeleton.tsx';
import { ActivityDialog, type ActivityDraft } from './activity-dialog.tsx';
import { ActivityList } from './activity-list.tsx';

/** The agenda of a deal, contact or property: pending first, then the done ones. */
export function ActivitiesCard({
  link,
  id,
  draft,
}: {
  link: RecordLink;
  id: string;
  /** Pre-filled links for "Nueva actividad". */
  draft: ActivityDraft;
}) {
  const activities = useRecordActivities(link, id);
  const canEdit = useCan('records.edit');
  const [creating, setCreating] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const pending = activities.data?.filter((a) => a.status === 'pending') ?? [];
  const done = activities.data?.filter((a) => a.status === 'done') ?? [];
  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">Actividades</h2>
        {canEdit && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus /> Nueva actividad
          </Button>
        )}
      </div>
      {activities.isPending ? (
        <Loading />
      ) : activities.error ? (
        <Alert tone="danger">{errorMessage(activities.error)}</Alert>
      ) : (
        <>
          <ActivityList items={pending} empty="Nada pendiente." showAssignee />
          {done.length > 0 && (
            <div className="space-y-2">
              <Button
                size="sm"
                variant="link"
                aria-expanded={showDone}
                onClick={() => {
                  setShowDone((v) => !v);
                }}
              >
                {showDone ? 'Ocultar completadas' : `Ver completadas (${String(done.length)})`}
              </Button>
              {showDone && <ActivityList items={done} empty="" showAssignee />}
            </div>
          )}
        </>
      )}
      {creating && (
        <ActivityDialog
          draft={draft}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </Card>
  );
}
